import {Component, OnDestroy, OnInit, ViewContainerRef, ViewEncapsulation} from '@angular/core';
import {merge, Subject} from 'rxjs';
import {AppLayoutService} from '@core/services/app-layout.service';
import {AppService} from '@core/services/app.service';
import {UserService} from '@core/services/user.service';
import {UserOnlineService} from '@core/services/user-online.service';
import {NavigationEnd, Router} from '@angular/router';
import {AuthService} from '@core/authentication/auth.service';
import {PermissionService} from '@core/authentication/permission.service';
import {NotifyService} from '@shared/services/global';
import {debounceTime, filter, takeUntil} from "rxjs/operators";
import {BusinessUserService} from '@app/shared/services/global/business-user.service';

/**
 * MobileApp — global JS object injected by the native iOS/Android WebView.
 * Chỉ tồn tại khi app chạy trong WebView của mobile app.
 * FE13 ref: app.component.ts L47-L49
 */
declare const MobileApp: { callPhoneNumber(phone: string): void } | undefined;

@Component({
  selector: 'app-root',
  template: `
 <router-outlet></router-outlet>`,
  standalone: false,
  styles: [`
 app-root {
 height: 100%
 }
 `],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {

  private isLoaded = false;
  private destroyed = new Subject();

  constructor(
    private _app: AppService,
    private appLayoutService: AppLayoutService,
    private router: Router,
    private _viewContainerRef: ViewContainerRef,
    private _authService: AuthService,
    private _userService: UserService,
    private notifyService: NotifyService,
    private permissionService: PermissionService,
    // Inject để khởi tạo BusinessUserService — tự động load nhân viên khi DN thay đổi
    private _businessUserService: BusinessUserService,
    // Inject để khởi tạo UserOnlineService — quản lý heartbeat + tab indicator
    private _userOnlineService: UserOnlineService,
  ) {

  }

  ngOnInit() {
    this._app.setViewRootContainerRef(this._viewContainerRef);

    // Gộp cả 2 luồng (Thay đổi Business và Chuyển trang) vào làm một để xử lý tập trung
    merge(
      this._authService.activeBusiness$,
      this.router.events.pipe(filter(event => event instanceof NavigationEnd))
    ).pipe(
      debounceTime(50), // Chống rung để không gọi API 2 lần liên tiếp khi khởi tạo app
      takeUntil(this.destroyed)
    ).subscribe(() => {
      this.initializePermissions(this._authService.activeBusiness);
      this.syncOnlineStatus();
    });
  }

  /**
   * Khởi tạo hoặc xóa quyền tùy vào URL và Business hiện tại
   * Logic:
   * - Trang Public (Login, Checkout...): Xóa quyền để đảm bảo layout hiển thị đúng chế độ công cộng.
   * - Trang Private: Gọi API nạp quyền nếu user dùng Custom Role.
   * (PermissionService đã có cơ chế cache nên sẽ không gọi API lặp lại nếu đã nạp rồi).
   */
  private initializePermissions(activeBusiness: any) {
    const url = this.router.url;
    // Danh sách các prefix URL được xem là trang công cộng (không cần nạp quyền quản trị)
    const isPublicPage = url.startsWith('/auth') || url.startsWith('/checkout') || url.startsWith('/pricing') ||
      url.startsWith('/privacy-policy') || url.startsWith('/privacy-terms');

    if (!isPublicPage && activeBusiness && this._authService.isCustomRole()) {
      // Thực hiện nạp quyền. Service sẽ tự quyết định có gọi API thật hay lấy từ cache dựa trên ID Business.
      this.permissionService.loadRolePermissions(activeBusiness.roleId, activeBusiness.id).subscribe();
    } else {
      // Nếu là trang public hoặc không phải custom role, xóa dữ liệu quyền hiện tại để tránh hiển thị Menu nhầm lẫn
      this.permissionService.clearPermissions();
    }
  }

  /**
   * Đồng bộ trạng thái online:
   * - Trang private + đã login → bắt đầu gửi heartbeat + hiển thị ● trên tab
   * - Trang public hoặc chưa login → dừng
   */
  private syncOnlineStatus() {
    const url = this.router.url;
    const isPublicPage = url.startsWith('/auth') || url.startsWith('/checkout') || url.startsWith('/pricing') ||
      url.startsWith('/privacy-policy') || url.startsWith('/privacy-terms');

    if (!isPublicPage && this._authService.isLoginEd()) {
      this._userOnlineService.start();
    } else {
      this._userOnlineService.stop();
    }
  }

  ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

}
